import { z } from 'zod';
import {
  ATTENDANCE_PAYABLE_FRACTION,
  AttendanceApproval,
  AttendancePeriodStatus,
  AttendanceStatus,
  ShiftType,
} from '../enums';
import { MAX_BULK_SIZE } from '../constants';
import { booleanish, dateSchema, paginationSchema, zEnum } from './common';

/** Statuses that credit no working hours, so hours must be zero for them. */
const NON_WORKING: AttendanceStatus[] = [
  AttendanceStatus.ABSENT,
  AttendanceStatus.UNPAID_LEAVE,
  AttendanceStatus.WEEKLY_OFF,
  AttendanceStatus.HOLIDAY,
  AttendanceStatus.PAID_LEAVE,
];

const hoursSchema = z.coerce.number().min(0).max(24);

/**
 * One worker, one day. The grain a supervisor actually marks.
 *
 * `hoursWorked` is separate from `status` on purpose: status decides what the
 * day *bills*, hours decide what overtime is owed on top. A PRESENT day with
 * 4 hours is still a full billable day under most contracts.
 */
export const markAttendanceSchema = z
  .object({
    deploymentId: z.string().uuid(),
    date: dateSchema,
    status: zEnum(AttendanceStatus).default(AttendanceStatus.PRESENT),
    shift: zEnum(ShiftType).optional(),
    hoursWorked: hoursSchema.default(8),
    overtimeHours: hoursSchema.default(0),
    inTime: dateSchema.optional(),
    outTime: dateSchema.optional(),
    remarks: z.string().trim().max(300).optional().or(z.literal('')),
  })
  .refine((v) => !NON_WORKING.includes(v.status) || v.hoursWorked === 0, {
    message: 'A non-working day cannot have worked hours',
    path: ['hoursWorked'],
  })
  .refine((v) => !NON_WORKING.includes(v.status) || v.overtimeHours === 0, {
    message: 'A non-working day cannot have overtime',
    path: ['overtimeHours'],
  })
  .refine((v) => v.hoursWorked + v.overtimeHours <= 24, {
    message: 'Total hours cannot exceed 24 in a day',
    path: ['overtimeHours'],
  })
  .refine((v) => !v.inTime || !v.outTime || v.outTime > v.inTime, {
    // Night shifts cross midnight, so the caller must send a full datetime for
    // out-time rather than a time-of-day that appears to precede in-time.
    message: 'Out-time must be after in-time',
    path: ['outTime'],
  });
export type MarkAttendanceInput = z.infer<typeof markAttendanceSchema>;

/**
 * The muster roll: one date, many people. This is how attendance is actually
 * entered — a supervisor walks the line once and marks everyone.
 */
export const markAttendanceBulkSchema = z.object({
  date: dateSchema,
  entries: z
    .array(
      z.object({
        deploymentId: z.string().uuid(),
        status: zEnum(AttendanceStatus).default(AttendanceStatus.PRESENT),
        shift: zEnum(ShiftType).optional(),
        hoursWorked: hoursSchema.optional(),
        overtimeHours: hoursSchema.default(0),
        remarks: z.string().trim().max(300).optional().or(z.literal('')),
      }),
    )
    .min(1)
    .max(MAX_BULK_SIZE),
});
export type MarkAttendanceBulkInput = z.infer<typeof markAttendanceBulkSchema>;

/** Marks every unmarked deployment at a site for a date, in one action. */
export const fillMusterSchema = z.object({
  date: dateSchema,
  companyId: z.string().uuid().optional(),
  locationId: z.string().uuid().optional(),
  status: zEnum(AttendanceStatus).default(AttendanceStatus.PRESENT),
  hoursWorked: hoursSchema.default(8),
  /** Leave already-marked days alone; only fill the blanks. */
  onlyUnmarked: booleanish.default(true),
});
export type FillMusterInput = z.infer<typeof fillMusterSchema>;

export const attendanceSearchSchema = paginationSchema.extend({
  companyId: z.string().uuid().optional(),
  locationId: z.string().uuid().optional(),
  deploymentId: z.string().uuid().optional(),
  from: dateSchema.optional(),
  to: dateSchema.optional(),
  status: zEnum(AttendanceStatus).optional(),
  approvalStatus: zEnum(AttendanceApproval).optional(),
});
export type AttendanceSearchQuery = z.infer<typeof attendanceSearchSchema>;

/** The muster grid: everyone at a site for one month. */
export const musterQuerySchema = z.object({
  companyId: z.string().uuid(),
  locationId: z.string().uuid().optional(),
  year: z.coerce.number().int().min(2020).max(2100),
  month: z.coerce.number().int().min(1).max(12),
});
export type MusterQuery = z.infer<typeof musterQuerySchema>;

/**
 * Backs the attendance and payroll exception queues.
 *
 * `companyId` is optional here, unlike the muster: an employer's own company is
 * implied, and back-office callers supply it. The service rejects the missing
 * case for a staff caller rather than silently scanning every client.
 */
export const exceptionsQuerySchema = z.object({
  companyId: z.string().uuid().optional(),
  year: z.coerce.number().int().min(2020).max(2100),
  month: z.coerce.number().int().min(1).max(12),
});
export type ExceptionsQuery = z.infer<typeof exceptionsQuerySchema>;

/**
 * Daily overtime above this is worth a human look.
 *
 * The Factories Act caps a working day (shift + overtime) at ten hours, so more
 * than two hours over an eight-hour shift is already at the statutory edge.
 * Four is set deliberately above that: this queue is for finding data-entry
 * slips and genuine excess, and a threshold that fires on every legitimately
 * busy day is a threshold nobody reads.
 */
export const DAILY_OVERTIME_FLAG_HOURS = 4;

/**
 * Monthly overtime above this is worth a human look.
 *
 * The Act's quarterly ceiling is fifty hours in most states, so a single month
 * past fifty is on course to breach the quarter. Flagged, not blocked — the
 * ceiling is quarterly and this service sees one month.
 */
export const MONTHLY_OVERTIME_FLAG_HOURS = 50;

export const approveAttendanceSchema = z.object({
  recordIds: z.array(z.string().uuid()).min(1).max(MAX_BULK_SIZE),
  approve: z.boolean(),
  reason: z.string().trim().max(300).optional(),
});
export type ApproveAttendanceInput = z.infer<typeof approveAttendanceSchema>;

export const periodActionSchema = z.object({
  companyId: z.string().uuid(),
  year: z.coerce.number().int().min(2020).max(2100),
  month: z.coerce.number().int().min(1).max(12),
  /** Required when reopening a locked period — it is an auditable exception. */
  reason: z.string().trim().max(500).optional(),
});
export type PeriodActionInput = z.infer<typeof periodActionSchema>;

// ---------------------------------------------------------------------------
// Derivations shared by the API, the invoice and the UI
// ---------------------------------------------------------------------------

export interface AttendanceTotals {
  /** Days marked, whatever the status. */
  marked: number;
  present: number;
  absent: number;
  leave: number;
  weeklyOff: number;
  holiday: number;
  /** Sum of ATTENDANCE_PAYABLE_FRACTION — what the client is billed for. */
  payableDays: number;
  overtimeHours: number;
}

/**
 * Rolls a set of daily marks into the figures an invoice line needs.
 *
 * Lives in shared so the monthly summary on screen and the invoice computed on
 * the server are literally the same arithmetic. A client querying a bill should
 * never be told a different number by the portal than by the invoice.
 */
export const sumAttendance = (
  records: { status: AttendanceStatus; overtimeHours?: number | string | null }[],
): AttendanceTotals => {
  const totals: AttendanceTotals = {
    marked: records.length,
    present: 0,
    absent: 0,
    leave: 0,
    weeklyOff: 0,
    holiday: 0,
    payableDays: 0,
    overtimeHours: 0,
  };

  for (const record of records) {
    totals.payableDays += ATTENDANCE_PAYABLE_FRACTION[record.status] ?? 0;
    totals.overtimeHours += Number(record.overtimeHours ?? 0);

    switch (record.status) {
      case AttendanceStatus.PRESENT:
      case AttendanceStatus.ON_DUTY:
        totals.present += 1;
        break;
      case AttendanceStatus.HALF_DAY:
        totals.present += 0.5;
        totals.absent += 0.5;
        break;
      case AttendanceStatus.ABSENT:
        totals.absent += 1;
        break;
      case AttendanceStatus.PAID_LEAVE:
      case AttendanceStatus.UNPAID_LEAVE:
        totals.leave += 1;
        break;
      case AttendanceStatus.WEEKLY_OFF:
        totals.weeklyOff += 1;
        break;
      case AttendanceStatus.HOLIDAY:
        totals.holiday += 1;
        break;
    }
  }

  // Floating-point addition of 0.5s drifts; days are quoted to one decimal.
  totals.payableDays = Math.round(totals.payableDays * 10) / 10;
  totals.present = Math.round(totals.present * 10) / 10;
  totals.absent = Math.round(totals.absent * 10) / 10;
  totals.overtimeHours = Math.round(totals.overtimeHours * 100) / 100;
  return totals;
};

/** Days in a calendar month. `month` is 1-based. */
export const daysInMonth = (year: number, month: number): number =>
  new Date(Date.UTC(year, month, 0)).getUTCDate();

/**
 * Whether a deployment was on the client's roll on a given date.
 *
 * A mid-month joiner must not appear absent for the days before they existed,
 * and someone who has exited must not keep accruing payable days. Getting this
 * wrong is how a manpower invoice bills for people who were not there.
 */
export const wasDeployedOn = (
  deployment: { joinedAt: Date | string; exitDate?: Date | string | null },
  date: Date,
): boolean => {
  const day = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  const joined = new Date(deployment.joinedAt);
  const joinedDay = Date.UTC(joined.getUTCFullYear(), joined.getUTCMonth(), joined.getUTCDate());
  if (day < joinedDay) return false;

  if (deployment.exitDate) {
    const exit = new Date(deployment.exitDate);
    const exitDay = Date.UTC(exit.getUTCFullYear(), exit.getUTCMonth(), exit.getUTCDate());
    if (day > exitDay) return false;
  }
  return true;
};

/** A period may only be edited before it is locked. */
export const isPeriodEditable = (status: AttendancePeriodStatus): boolean =>
  status !== AttendancePeriodStatus.LOCKED;

// ---------------------------------------------------------------------------
// The period state machine
//
// OPEN → SUBMITTED → APPROVED → LOCKED, plus an audited reopen from any closed
// state back to OPEN. This lives here rather than in the API service because
// billing asks the same questions of it, and two copies of a rule that decides
// what may be invoiced is one copy too many.
// ---------------------------------------------------------------------------

export type PeriodAction = 'submit' | 'approve' | 'lock' | 'reopen';

/** The status each action moves a period into. */
export const PERIOD_ACTION_RESULT: Record<PeriodAction, AttendancePeriodStatus> = {
  submit: AttendancePeriodStatus.SUBMITTED,
  approve: AttendancePeriodStatus.APPROVED,
  lock: AttendancePeriodStatus.LOCKED,
  reopen: AttendancePeriodStatus.OPEN,
};

/** The status an action must start from. `reopen` is the exception — see below. */
const PERIOD_ACTION_REQUIRES: Record<PeriodAction, AttendancePeriodStatus> = {
  submit: AttendancePeriodStatus.OPEN,
  approve: AttendancePeriodStatus.SUBMITTED,
  lock: AttendancePeriodStatus.APPROVED,
  reopen: AttendancePeriodStatus.OPEN, // unused; reopen is handled explicitly
};

export type PeriodTransitionCheck =
  | { ok: true; next: AttendancePeriodStatus }
  | { ok: false; reason: string };

/**
 * Decides whether a period may take an action, and says why not when it may not.
 *
 * Pure — the caller supplies the two facts that come from the database
 * (`unmarkedDays`, and the reason text on a reopen) so this can be tested and
 * reused without one.
 */
export const checkPeriodTransition = (
  current: AttendancePeriodStatus,
  action: PeriodAction,
  context: { unmarkedDays?: number; reason?: string | null } = {},
): PeriodTransitionCheck => {
  if (action === 'reopen') {
    if (current === AttendancePeriodStatus.OPEN) {
      return { ok: false, reason: 'This month is already open.' };
    }
    // Reopening a closed month means an invoice may already be out; the reason
    // is the audit trail for why the figures changed.
    if (!context.reason?.trim()) {
      return { ok: false, reason: 'Give a reason for reopening a closed month.' };
    }
    return { ok: true, next: AttendancePeriodStatus.OPEN };
  }

  const required = PERIOD_ACTION_REQUIRES[action];
  if (current !== required) {
    return {
      ok: false,
      reason:
        action === 'submit'
          ? `This month is already ${current.toLowerCase()}.`
          : action === 'approve'
            ? 'Only a submitted month can be approved.'
            : 'Only an approved month can be locked for billing.',
    };
  }

  // A month with holes in the muster cannot be billed from, so it cannot be
  // submitted either — the hole has to be resolved while people still remember.
  if (action === 'submit' && (context.unmarkedDays ?? 0) > 0) {
    const n = context.unmarkedDays as number;
    return {
      ok: false,
      reason: `${n} day(s) are still unmarked. Complete the muster before submitting.`,
    };
  }

  return { ok: true, next: PERIOD_ACTION_RESULT[action] };
};

/**
 * Whether a month's attendance may be turned into an invoice.
 *
 * Only a locked month qualifies. An approved-but-unlocked month can still move,
 * and billing from figures that can still move is how credit notes are born.
 */
export const isBillable = (status: AttendancePeriodStatus): boolean =>
  status === AttendancePeriodStatus.LOCKED;
